<template>
  <div class="container">
    <h1>Statistic by Country</h1>
    <div class="row margin-bottom-2">
      <div class="col-xs-4">
        <DevAppDropDownComponent role="sms" @devapp-changed="devAppChanged"/>
      </div>
    </div>

    <div id="sms-country" v-if="showSmsCountry">

      <div class="row margin-bottom-2 sms-country-filter">
        <div class="col-xs-2">
          <sdx-select
              data-testid="statusDropDown"
              label="Status"
              placeholder="Choose..."
              :value="statusFilter"
              @input="statusFilterChange"
          >
            <sdx-select-option
                v-for="(statusElem, idx) in statusList"
                data-testid="statusDropDownOption"
                v-bind:value="statusElem"
                v-bind:key="idx"
            >
              {{ statusElem }}
            </sdx-select-option>
          </sdx-select>
        </div>
        <div style="padding-right: 10px; padding-top: 3px; width: 240px">
          <span style="padding-left: 1px; padding-right: 5px; font-size: 15px">Sent Since</span>
          <sdx-menu-flyout>
            <sdx-menu-flyout-toggle>
              <sdx-icon icon-name="icon-information-circle" sr-hint="Read more" data-testid="infoSentSince"></sdx-icon>
            </sdx-menu-flyout-toggle>
            <sdx-menu-flyout-content style="width: 600px;">
              <p>
                SMS data exist only for 6 months back.
              </p>
            </sdx-menu-flyout-content>
          </sdx-menu-flyout>
          <sdx-input
              name="outboxFromDate"
              data-testid="sentSince"
              type="date"
              placeholder="01.01.2025 00:00:00"
              flatpickr-options='{"maxDate":"today", "dateFormat":"d.m.Y H:i:S", "locale":"de", "enableTime":"true", "enableSeconds":"true", "time_24hr":"true"}'
              :value="dateRange.fromDate"
              @input="fromDateChange"
          />
        </div>
        <!-- https://flatpickr.js.org/options/ -->
        <div style="padding-right: 0; padding-top: 3px; width: 240px">
          <span style="padding-left: 1px; padding-right: 5px; font-size: 15px">Sent Until</span>
          <sdx-input
              name="outboxToDate"
              data-testid="sentUntil"
              type="date"
              placeholder="31.01.2025 23:59:59"
              flatpickr-options='{"maxDate":"today", "dateFormat":"d.m.Y H:i:S", "locale":"de", "enableTime":"true", "enableSeconds":"true", "time_24hr":"true", "defaultHour":"23", "defaultMinute":"59", "defaultSecond":"59"}'
              :value="dateRange.toDate"
              @input="toDateChange"
          />
        </div>
      </div>

      <sdx-loading-spinner v-if="isLoading" id="spinner-sms-country-content" size="large"
                           sr-hint="Loading. Please wait."></sdx-loading-spinner>

      <div id="sms-country-content">
        <div v-if="smsCountry.length > 0" class="sms-country-table table table--responsive table--highlight margin-v-2">
          <div class="table_wrapper">
            <table data-init="auto">
              <thead>
              <tr>
                <th class="table-header">Prefix/Country</th>
                <th class="table-header">#SMS</th>
                <th v-if="userService.isAtLeastAdminWithoutFmg()" class="table-header">Top PhoneNumbers</th>
              </tr>
              </thead>
              <tbody>
              <tr v-for="(item, index) in smsCountry" :key="item.countryCode">
                <td class="table-cell">
                  <FlagComponent :country="item.countryCode"/>
                  {{ item.prefix }} - {{ item.countryName }}
                </td>
                <td>{{ formatBigNumber(item.smsCount) }}</td>
                <td v-if="userService.isAtLeastAdminWithoutFmg()" ref="topPhoneNumbersCell">
                  <sdx-button
                      theme="transparent"
                      icon-name="icon-chevron-normal-down"
                      icon-size="2"
                      label="Load top phonenumbers"
                      sr-hint="Top phonenumbers will only be loaded on demand."
                      @click=" loadTopPhoneNumbers(item.countryCode,index)"
                  ></sdx-button>
                </td>
              </tr>
              </tbody>
            </table>
          </div>
        </div>
        <div v-else class="no-items-found">
          <p><i>No SMS country data found</i></p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import smsCountry from '../service'
import loadingSpinnerService from '@/services/loading-spinner.service';
import DevAppDropDownComponent from "@/components/DevAppDropDownComponent";
import {showAlert} from "@/services/notification.service";
import FlagComponent from "@/components/FlagComponent.vue";
import {logger} from "@/services/logger.service";
import {addDate, formatBigNumber, formatDate, json2Table} from "@/util/utility";
import envService from "@/services/env.service";
import dateService from "@/services/date.service";
import userService from "@/services/user.service";

export default {
  components: {
    FlagComponent,
    DevAppDropDownComponent,
  },
  data() {
    return {
      webId: undefined,
      smsCountry: [],
      showSmsCountry: false,
      isLoading: true,
      statusFilter: [],
      dateRange: {
        fromDate: dateService.getInitialFromDate(formatDate(addDate(new Date(), -envService.getDays4SmsSearch(), "days").setHours(0, 0, 0, 0))),
        formattedFromDate: undefined,
        toDate: dateService.getInitialToDate(formatDate(new Date().setHours(23, 59, 59, 999))),
        formattedToDate: undefined
      },
    }
  },
  computed: {
    userService() {
      return userService
    },
    statusList: function () {
      return ['PROCESSING', 'DELIVERED', 'CONFIRMED', 'FAILURE', 'NO_INFO', 'BLOCKED']
    }
  },
  mounted() {
    dateService.updateFromDate(this.dateRange);
    dateService.updateToDate(this.dateRange);
    logger.debug("Dates updated:", this.dateRange);
  },
  methods: {
    formatBigNumber,
    async devAppChanged(webId) {
      logger.debug("DevApp changed to webId:", webId);
      this.webId = webId;
      await this.loadData();
    },
    async loadData() {
      logger.debug("Fetching SMS country data for webId:", this.webId);
      this.showSmsCountry = true;
      this.isLoading = true;
      loadingSpinnerService.show("sms-country-content");

      if (!this.webId) {
        logger.warn("No webId provided. Skipping API call.");
        this.showSmsCountry = false;
        this.isLoading = false;
        loadingSpinnerService.hide("sms-country-content");
        return;
      }

      try {
        let response = await smsCountry.getSmsCountry(this.webId, this.statusFilter, this.dateRange.formattedFromDate, this.dateRange.formattedToDate);

        this.smsCountry = response.data;
        this.showSmsCountry = true;

      } catch (error) {
        logger.debug("Error fetching SMS data:", error);
        showAlert("Error loading SMS data: " + error.message);
        this.smsCountry = [];
        this.showSmsCountry = false;
      }

      this.isLoading = false;
      loadingSpinnerService.hide("sms-country-content");
    },
    async loadTopPhoneNumbers(countryCode, index) {
      try {
        let response = await smsCountry.getTopPhoneNumbers(this.webId, countryCode, this.statusFilter, this.dateRange.formattedFromDate, this.dateRange.formattedToDate, 5);
        this.$refs.topPhoneNumbersCell[index].innerHTML = json2Table( response.data);
      } catch (error) {
        logger.debug("Error fetching top phone numbers:", error);
        showAlert("Error loading top phone numbers: " + error.message);
        this.$refs.topPhoneNumbersCell[index].innerText = "Error fetching top phone numbers";
      }
    },
    statusFilterChange: function () {
      this.statusFilter = arguments[0].target.value;
      this.loadData();
    },
    fromDateChange: async function () {
      await dateService.fromDateChange(arguments[0].target.value, this.dateRange);
      await this.loadData();
    },
    toDateChange: async function () {
      await dateService.toDateChange(arguments[0].target.value, this.dateRange);
      await this.loadData();
    },
  }
};
</script>

<style>
.sms-country-table {
  padding-top: 30px;
}

.sms-country-table .table-header {
  padding-top: 20px;
  padding-bottom: 17px;
}

.sms-country-table .table-cell {
  padding-top: 17px;
  padding-bottom: 2px;
  display: flex;
  align-items: center;
  gap: 10px;
}

.no-items-found {
  text-align: center;
  font-style: italic;
  margin-top: 10px;
}
</style>
